// Entry reveals on scroll (v4 motion): a block marked data-reveal fades up a
// short distance as it enters the viewport, and the children of a
// data-reveal-stagger block follow one another. The hidden starting state is
// only styled once this module has armed the document (READY_ATTRIBUTE), so
// crawlers, visitors without JavaScript and reduced-motion visitors always
// see the copy. Blocks already on screen are marked visible in the same
// observer pass that arms the document, so nothing showing ever blinks out,
// and each block reveals once. Blocks inside a chapter the scroll
// choreography has claimed (hooks.ts) are left to it.
import { unclaimed } from "@lib/marketing/hooks";

export const READY_ATTRIBUTE = "data-reveal-ready";
export const VISIBLE_CLASS = "is-visible";
export const STAGGER_VARIABLE = "--mk-i";

export interface RevealHandle {
  dispose(): void;
}

export function targets(root: ParentNode = document): HTMLElement[] {
  return unclaimed(
    Array.from(root.querySelectorAll<HTMLElement>("[data-reveal], [data-reveal-stagger]")),
  );
}

export function prefersReducedMotion(): boolean {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function mount(
  root: Document = document,
  elements: HTMLElement[] = targets(root),
): RevealHandle {
  for (const element of elements) {
    if (!element.hasAttribute("data-reveal-stagger")) continue;
    Array.from(element.children).forEach((child, index) => {
      (child as HTMLElement).style.setProperty(STAGGER_VARIABLE, String(index));
    });
  }

  let armed = false;
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        entry.target.classList.add(VISIBLE_CLASS);
        observer.unobserve(entry.target);
      }
      if (!armed) {
        armed = true;
        root.documentElement.setAttribute(READY_ATTRIBUTE, "");
      }
    },
    { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
  );
  for (const element of elements) observer.observe(element);

  return {
    dispose() {
      observer.disconnect();
      root.documentElement.removeAttribute(READY_ATTRIBUTE);
    },
  };
}

// Arms the reveals unless there is nothing to reveal or the visitor prefers
// reduced motion; if that preference is switched on later, everything is
// shown again.
export function boot(root: Document = document): RevealHandle | null {
  const elements = targets(root);
  if (elements.length === 0 || prefersReducedMotion() || !("IntersectionObserver" in window))
    return null;

  const handle = mount(root, elements);
  const query = window.matchMedia("(prefers-reduced-motion: reduce)");
  const onChange = (event: MediaQueryListEvent) => {
    if (!event.matches) return;
    handle.dispose();
    query.removeEventListener("change", onChange);
  };
  query.addEventListener("change", onChange);
  return handle;
}
